'use client';

import {
    AUTH_STATE_STORAGE_KEY,
    AUTH_TOKEN_STORAGE_KEY,
    clearStoredAuthTokens,
    getCookieValue,
} from './auth-storage';

type RequestHeaders = HeadersInit | Record<string, string | null | undefined>;

type ApiRequestConfig = {
    headers?: RequestHeaders;
    signal?: AbortSignal;
    timeout?: number;
};

type ApiResponse<T> = {
    data: T;
    status: number;
    statusText: string;
    headers: Headers;
};

class ApiError<T = unknown> extends Error {
    response?: ApiResponse<T>;

    constructor(message: string, response?: ApiResponse<T>) {
        super(message);
        this.name = 'ApiError';
        this.response = response;
    }
}

function getRuntimeConfig() {
    if (typeof window === 'undefined') {
        return null;
    }

    return window.__APP_CONFIG__ ?? null;
}

const defaults = {
    baseURL: '',
    timeout: 30000,
    headers: {
        'Content-Type': 'application/json',
    },
};

function getApiKey() {
    return getRuntimeConfig()?.apiKey?.trim() || null;
}

function getStoredAuthToken() {
    if (typeof window === 'undefined') {
        return null;
    }

    const directToken = getCookieValue(AUTH_TOKEN_STORAGE_KEY);
    if (directToken) {
        return directToken;
    }

    const serializedAuthState = getCookieValue(AUTH_STATE_STORAGE_KEY);
    if (!serializedAuthState) {
        return null;
    }

    try {
        const parsedState = JSON.parse(serializedAuthState) as { authToken?: unknown };
        const stateToken = typeof parsedState.authToken === 'string' ? parsedState.authToken.trim() : '';
        return stateToken || null;
    } catch {
        return null;
    }
}

function setHeader(headers: Headers, name: string, value: string | null) {
    if (value) {
        headers.set(name, value);
        return;
    }

    headers.delete(name);
}

function applyHeaders(target: Headers, source?: RequestHeaders) {
    if (!source) {
        return;
    }

    if (source instanceof Headers) {
        source.forEach((value, key) => {
            target.set(key, value);
        });
        return;
    }

    if (Array.isArray(source)) {
        source.forEach(([key, value]) => {
            if (value == null) {
                target.delete(key);
                return;
            }

            target.set(key, value);
        });
        return;
    }

    Object.entries(source).forEach(([key, value]) => {
        if (value == null) {
            target.delete(key);
            return;
        }

        target.set(key, value);
    });
}

function getRequestHeaders(config: ApiRequestConfig | undefined, body: BodyInit | undefined) {
    const headers = new Headers();
    applyHeaders(headers, defaults.headers);

    setHeader(headers, 'apikey', getApiKey());

    const token = getStoredAuthToken();
    setHeader(headers, 'Authorization', token ? `Bearer ${token}` : null);
    applyHeaders(headers, config?.headers);

    if (body instanceof FormData) {
        headers.delete('Content-Type');
    }

    return headers;
}

function getBaseUrl() {
    return defaults.baseURL || getRuntimeConfig()?.apiUrl || '';
}

function resolveUrl(url: string) {
    if (/^https?:\/\//i.test(url)) {
        return url;
    }

    const baseUrl = getBaseUrl();
    if (!baseUrl) {
        return url;
    }

    const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    const normalizedUrl = url.startsWith('/') ? url.slice(1) : url;

    return new URL(normalizedUrl, normalizedBaseUrl).toString();
}

function getRequestBody(body: unknown, headers: Headers): BodyInit | undefined {
    if (body == null) {
        return undefined;
    }

    if (
        body instanceof FormData ||
        body instanceof URLSearchParams ||
        body instanceof Blob ||
        body instanceof ArrayBuffer ||
        typeof body === 'string'
    ) {
        return body;
    }

    if (ArrayBuffer.isView(body)) {
        return body as unknown as BodyInit;
    }

    if (!headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
    }

    return JSON.stringify(body);
}

async function parseResponseBody(response: Response) {
    const text = await response.text();
    if (!text) {
        return null;
    }

    const trimmed = text.trim();
    const contentType = response.headers.get('Content-Type') ?? '';
    const isJson =
        contentType.includes('application/json') ||
        contentType.includes('+json') ||
        trimmed.startsWith('{') ||
        trimmed.startsWith('[');

    if (!isJson) {
        return text;
    }

    try {
        return JSON.parse(text);
    } catch {
        return text;
    }
}

function linkAbortSignal(controller: AbortController, signal?: AbortSignal) {
    if (!signal) {
        return () => undefined;
    }

    if (signal.aborted) {
        controller.abort(signal.reason);
        return () => undefined;
    }

    const abortHandler = () => controller.abort(signal.reason);
    signal.addEventListener('abort', abortHandler, { once: true });
    return () => signal.removeEventListener('abort', abortHandler);
}

async function request<T>(method: string, url: string, body?: unknown, config?: ApiRequestConfig): Promise<ApiResponse<T>> {
    const controller = new AbortController();
    const unlinkAbortSignal = linkAbortSignal(controller, config?.signal);
    const headers = getRequestHeaders(config, body instanceof FormData ? body : undefined);
    const requestBody = getRequestBody(body, headers);
    const timeout = window.setTimeout(() => controller.abort(new DOMException('Request timed out', 'TimeoutError')), config?.timeout ?? defaults.timeout);

    try {
        const response = await fetch(resolveUrl(url), {
            method,
            headers,
            body: requestBody,
            signal: controller.signal,
        });

        const data = await parseResponseBody(response);
        const apiResponse: ApiResponse<T> = {
            data: data as T,
            status: response.status,
            statusText: response.statusText,
            headers: response.headers,
        };

        if (response.status === 401) {
            clearStoredAuthTokens();
        }

        if (!response.ok) {
            throw new ApiError(response.statusText || `Request failed with status ${response.status}`, apiResponse);
        }

        return apiResponse;
    } finally {
        unlinkAbortSignal();
        clearTimeout(timeout);
    }
}

const api = {
    defaults,
    get<T>(url: string, config?: ApiRequestConfig) {
        return request<T>('GET', url, undefined, config);
    },
    post<T>(url: string, body?: unknown, config?: ApiRequestConfig) {
        return request<T>('POST', url, body, config);
    },
    put<T>(url: string, body?: unknown, config?: ApiRequestConfig) {
        return request<T>('PUT', url, body, config);
    },
    patch<T>(url: string, body?: unknown, config?: ApiRequestConfig) {
        return request<T>('PATCH', url, body, config);
    },
    delete<T>(url: string, config?: ApiRequestConfig) {
        return request<T>('DELETE', url, undefined, config);
    }
};

export default api;